import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CloseOutlined,
  InfoCircleFilled,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Badge, Button, Popover, Tooltip, Typography } from 'antd';
import { useActionPlanAssignBannerStore } from '@/store/actionPlanAssignBannerStore';
import { CONSTANTS } from '@/utils/constants';
import { toSafeHtml } from '@/utils/sanitizeHtml'
const NOTIFY = CONSTANTS.ACTION_PLAN.NOTIFY;

/** Banner ngang — portal lên body. */
const ActionPlanAssignBannerBar: React.FC<{
  bannerText: string;
  canReload: boolean;
  reloading: boolean;
  onReload: () => void;
  onDismiss: () => void;
}> = ({ bannerText, canReload, reloading, onReload, onDismiss }) => (
  <div
    className="action-plan-assign-banner-root fixed left-0 right-0 top-3 z-[1100] flex justify-center px-4 pointer-events-none"
    role="status"
    aria-live="polite"
  >
    <div
      className={[
        'pointer-events-auto relative grid h-11 w-full max-w-3xl grid-cols-[auto_1fr_auto] items-center overflow-hidden',
        'rounded-xl border border-[#69b1ff] bg-white',
        'shadow-[0_10px_30px_rgba(22,119,255,0.18)]',
      ].join(' ')}
    >
      <span className="absolute inset-y-0 left-0 z-[1] w-1.5 rounded-l-xl bg-[#1677ff]" aria-hidden />
      <span className="relative z-[2] flex h-11 w-11 shrink-0 items-center justify-center pl-1 text-[#1677ff]">
        <InfoCircleFilled className="!m-0 text-[17px] leading-none" />
      </span>
      <span className="relative z-[2] min-w-0 truncate px-1 text-[13px] font-semibold leading-snug text-[#003eb3]">
        {bannerText}
      </span>
      <div className="relative z-[2] flex h-11 shrink-0 items-center gap-0.5 pr-1.5">
        {canReload && (
          <Tooltip title={NOTIFY.RELOAD_BTN}>
            <Button
              type="primary"
              size="small"
              className="!h-8 !rounded-lg !px-2.5"
              icon={<ReloadOutlined />}
              loading={reloading}
              onClick={onReload}
            >
              <span className="hidden sm:inline">{NOTIFY.RELOAD_BTN}</span>
            </Button>
          </Tooltip>
        )}
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent text-[#8c8c8c] hover:bg-[#f5f5f5] hover:text-[#262626]"
          onClick={onDismiss}
          aria-label={NOTIFY.DISMISS_BTN}
        >
          <CloseOutlined className="!m-0 text-xs leading-none" />
        </button>
      </div>
    </div>
  </div>
);

/**
 * Icon trên header — cùng pattern Buoc6ChiTieuWarningHeaderNotify
 * (vòng kem + WarningOutlined + Badge + ping nhấp nháy).
 */
const ActionPlanAssignHeaderNotify: React.FC<{
  title: string;
  body: string;
  canReload: boolean;
  reloading: boolean;
  onReload: () => void;
}> = ({ title, body, canReload, reloading, onReload }) => {
  const [open, setOpen] = useState(true);
  const displayTitle = title || NOTIFY.FALLBACK_TITLE;

  const content = (
    <div style={{ width: 320 }} onClick={(e) => e.stopPropagation()}>
      {body ? (
        <Typography.Paragraph
          style={{ marginBottom: 12, fontSize: 14, color: '#595959', lineHeight: 1.5 }}
        >
          <span dangerouslySetInnerHTML={toSafeHtml(body)} />
        </Typography.Paragraph>
      ) : (
        <Typography.Paragraph
          type="secondary"
          style={{ marginBottom: 12, fontSize: 14 }}
          >
          <span dangerouslySetInnerHTML={toSafeHtml(NOTIFY.FALLBACK_BODY)} />
        </Typography.Paragraph>
      )}
      <div
        style={{
          marginTop: 12,
          paddingTop: 12,
          borderTop: '0.5px solid #f0f0f0',
          display: 'flex',
          flexDirection: 'row',
          gap: 8,
        }}
      >
        {canReload && (
          <Button
            type="primary"
            size="middle"
            block
            icon={<ReloadOutlined />}
            loading={reloading}
            onClick={onReload}
          >
            {NOTIFY.RELOAD_BTN}
          </Button>
        )}
        <Button size="middle" block onClick={() => setOpen(false)}>
          {NOTIFY.DISMISS_BTN}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes ap-assign-ping { 
          0% { transform: scale(1); opacity: 0.55; }
          75%, 100% { transform: scale(1.85); opacity: 0; }
        }
        .ap-assign-ping {
          animation: ap-assign-ping 1.25s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .ap-assign-ping.is-paused {
          animation: none;
          opacity: 0;
        }
      `}</style>
      <Popover
        open={open}
        onOpenChange={(next) => {
          // Không đóng content khi bấm ra ngoài — chỉ mở lại khi click icon.
          if (next) setOpen(true);
        }}
        trigger="click"
        placement="bottomRight"
        title={displayTitle}
        content={content}
        zIndex={1200}
      >
        <Tooltip title={displayTitle} placement="bottom">
          <div
            className="relative cursor-pointer transition-transform duration-200 hover:scale-110 hover:-translate-y-0.5"
            role="button"
            tabIndex={0}
            aria-label={displayTitle}
          >
            {/* Vòng ping nhấp nháy phía sau */}
            <span
              className={[
                'ap-assign-ping pointer-events-none absolute inset-0 rounded-full bg-[#FAAD14]',
                open ? 'is-paused' : '',
              ].join(' ')}
              aria-hidden
            />
            <Badge count={1} size="small" offset={[-2, 2]} color="#FAAD14">
              <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF7E6] text-[#FAAD14]">
                <WarningOutlined style={{ fontSize: 18 }} />
              </div>
            </Badge>
          </div>
        </Tooltip>
      </Popover>
    </>
  );
};

/** Thông báo assign — `UI_MODE`: `'popover'` (icon header) | `'banner'` (thanh ngang). */
export const ActionPlanAssignBanner: React.FC = () => {
  const banner = useActionPlanAssignBannerStore((s) => s.banner);
  const dismissBanner = useActionPlanAssignBannerStore((s) => s.dismissBanner);
  const reloadHandler = useActionPlanAssignBannerStore((s) => s.reloadHandler);
  const triggerReload = useActionPlanAssignBannerStore((s) => s.triggerReload);
  const [mounted, setMounted] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!banner || !mounted) return null;

  const bannerText = [banner.title, banner.body].filter(Boolean).join(' — ');
  const canReload = typeof reloadHandler === 'function';

  const handleReloadClick = async () => {
    if (!canReload || reloading) return;
    setReloading(true);
    try {
      await triggerReload();
    } finally {
      setReloading(false);
    }
  };

  if (NOTIFY.UI_MODE === 'popover') {
    // Render inline trong hàng icon header (không portal / không fixed).
    return (
      <ActionPlanAssignHeaderNotify
        title={banner.title}
        body={banner.body}
        canReload={canReload}
        reloading={reloading}
        onReload={handleReloadClick}
      />
    );
  }

  return createPortal(
    <ActionPlanAssignBannerBar
      bannerText={bannerText}
      canReload={canReload}
      reloading={reloading}
      onReload={handleReloadClick}
      onDismiss={dismissBanner}
    />,
    document.body
  );
};
